import {
  formatVariableRef,
  parseVariableRef,
} from '../../../../utils/variableScope';
import { evaluateDocMath } from '../../../../utils/mathExpr';
import {
  forEachMathValueToken,
  wrapMathValueToken,
} from '../../../../utils/mathValueToken';
import {
  PILL_DISPLAY_MAX,
  truncateMiddle,
} from '../../../DocEditor/utils/variableUtils/pillRefresh';
import { joinPathLineBreaks } from '../../../../utils/variableScope/resolve';

const escapeHtml = (text = '') =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const paintMathPill = (span, expr, resolveVariable) => {
  const formula = String(expr ?? '').trim();
  const { state, text, color, tip } = evaluateDocMath(formula, resolveVariable);
  span.className = 'doc-variable';
  span.setAttribute('data-doc-math', '');
  span.setAttribute('data-math-expr', formula);
  span.setAttribute('data-var-state', state);
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('spellcheck', 'false');
  if (color) span.setAttribute('style', `--var-color: ${color};`);
  else span.removeAttribute('style');
  if (tip) span.setAttribute('data-var-tip', tip);
  else span.removeAttribute('data-var-tip');
  span.textContent = text || formula || 'math';
  return span;
};

const VAR_TOKEN_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

const varsOnlyToHtml = (raw, resolveVariable) => {
  const text = String(raw ?? '');
  if (!text) return '';
  let html = '';
  let last = 0;
  const re = new RegExp(VAR_TOKEN_RE.source, 'g');
  let m;
  while ((m = re.exec(text))) {
    html += escapeHtml(text.slice(last, m.index)).replace(/\n/g, '<br>');
    const { name, index, ref } = parseVariableRef(m[1]);
    const hit = (name && resolveVariable?.(ref)) || null;
    const color = hit?.color || 'rgba(236, 240, 245, 0.75)';
    const label = formatVariableRef(name, index) || ref;
    const rawDisplay =
      hit && String(hit.value ?? '').trim() !== ''
        ? joinPathLineBreaks(String(hit.value), { joinInternal: true })
        : `{{${label}}}`;
    const display =
      rawDisplay.length > PILL_DISPLAY_MAX
        ? truncateMiddle(rawDisplay, PILL_DISPLAY_MAX)
        : rawDisplay;
    const title = hit
      ? `${label}${hit.sourceName ? ` — ${hit.sourceName}` : ''}`
      : `${label} — unresolved`;
    html +=
      `<span class="variables-panel__nested-var" data-var-ref="${escapeHtml(label)}" ` +
      `contenteditable="false" spellcheck="false" style="color:${escapeHtml(color)}" ` +
      `title="${escapeHtml(title)}">${escapeHtml(display)}</span>\u200B`;
    last = m.index + m[0].length;
  }
  html += escapeHtml(text.slice(last)).replace(/\n/g, '<br>');
  return html;
};

const mathTokenToHtml = (expr, resolveVariable) => {
  const formula = String(expr ?? '').trim();
  const { state, text, color } = evaluateDocMath(formula, resolveVariable);
  const style = color ? ` style="--var-color: ${escapeHtml(color)};"` : '';
  return (
    `<span class="doc-variable" data-doc-math data-math-expr="${escapeHtml(formula)}" ` +
    `data-var-state="${escapeHtml(state)}" contenteditable="false" spellcheck="false"${style}>` +
    `${escapeHtml(text || formula || 'math')}</span>\u200B`
  );
};

/** Stored string → HTML with nested-var chips and live math pills. */
export const nestedValueToHtml = (raw, resolveVariable) => {
  const text = String(raw ?? '');
  if (!text) return '';
  if (!text.includes('{{=')) return varsOnlyToHtml(text, resolveVariable);
  let html = '';
  let last = 0;
  forEachMathValueToken(text, ({ expr, start, end }) => {
    html += varsOnlyToHtml(text.slice(last, start), resolveVariable);
    html += mathTokenToHtml(expr, resolveVariable);
    last = end;
  });
  html += varsOnlyToHtml(text.slice(last), resolveVariable);
  return html;
};

/**
 * Above this, the value cell keeps the rest off the DOM. The field is only
 * a few lines tall; laying out a multi-megabyte contenteditable (especially
 * one `<div>` per pasted line) freezes the tab.
 */
export const MAX_VALUE_DOM_CHARS = 24000;

/**
 * A resting cell is only ~5 lines tall. Mounting the whole value (up to the
 * edit cap) makes every visible row lay out thousands of characters while
 * scrolling. The rest stays in the tail and comes back on focus.
 */
export const RESTING_DOM_CHARS = 480;

/** Snap the cut so a `{{token}}` isn't sliced in half. */
export const splitLargeValue = (raw, max = MAX_VALUE_DOM_CHARS) => {
  const text = String(raw ?? '');
  if (text.length <= max) return { head: text, tail: '' };
  let cut = max;
  const lead = text.charCodeAt(cut - 1);
  if (lead >= 0xd800 && lead <= 0xdbff) cut -= 1;
  const from = Math.max(0, cut - 300);
  const slice = text.slice(from, cut);
  const open = slice.lastIndexOf('{{');
  const close = slice.lastIndexOf('}}');
  if (open !== -1 && (close === -1 || open > close)) {
    const snapped = from + open;
    if (snapped >= Math.floor(max * 0.5)) cut = snapped;
  }
  if (cut <= 0) cut = Math.min(max, text.length);
  return { head: text.slice(0, cut), tail: text.slice(cut) };
};

export const largeTailLabel = (count) => {
  const grouped = String(Math.max(0, count)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `… ${grouped} more`;
};

export const appendLargeTailMarker = (root, tailLength) => {
  const marker = document.createElement('span');
  marker.setAttribute('data-large-tail', '');
  marker.setAttribute('contenteditable', 'false');
  marker.setAttribute('spellcheck', 'false');
  marker.className = 'variables-panel__large-tail';
  marker.title = 'The rest of this value is saved. Delete this to drop it.';
  marker.textContent = ` ${largeTailLabel(tailLength)}`;
  root.appendChild(marker);
  return marker;
};

/**
 * Paint `raw` into `el`. Returns the hidden tail (empty when it all fits).
 * Plain text stays one text node — `<br>` per line makes select-all / cut
 * walk thousands of elements.
 */
export const paintNestedValue = (el, raw, resolveVariable, max = MAX_VALUE_DOM_CHARS) => {
  if (!el) return '';
  const { head, tail } = splitLargeValue(raw, max);
  if (!head.includes('{{')) {
    el.textContent = head;
  } else {
    el.innerHTML = nestedValueToHtml(head, resolveVariable) || '';
  }
  if (tail) appendLargeTailMarker(el, tail.length);
  return tail;
};

export const splicePlainText = (value, start, end, insert) => {
  const text = String(value ?? '');
  const a = Math.max(0, Math.min(start, text.length));
  const b = Math.max(a, Math.min(end, text.length));
  return text.slice(0, a) + String(insert ?? '') + text.slice(b);
};

const pointInside = (node, stop) =>
  !!stop &&
  (node === stop.container ||
    (node.nodeType === Node.ELEMENT_NODE && node.contains(stop.container)));

const isZwspOnly = (node) =>
  node?.nodeType === Node.TEXT_NODE && !(node.textContent || '').replace(/\u200B/g, '');

const neighbor = (node, dir) => {
  let sib = node?.[dir];
  while (isZwspOnly(sib)) sib = sib[dir];
  return sib || null;
};

const isAtomicChip = (node) =>
  node?.nodeType === Node.ELEMENT_NODE &&
  !!(node.dataset?.varRef || node.dataset?.mathExpr || node.hasAttribute?.('data-doc-math'));

const leadText = (node) => {
  if (!node) return '';
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent || '').replace(/^\u200B+/, '');
  }
  if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'BR') {
    return (node.textContent || '').replace(/^\u200B+/, '');
  }
  return '';
};

const isPathContinuation = (node) => /^[\\/]/.test(leadText(node));

/**
 * Serialize like `nestedHtmlToValue`, stopping at a DOM point.
 * `stop` is `{ container, offset }` from a selection boundary.
 */
const serializeUntil = (root, stop) => {
  if (!root) return '';
  let out = '';
  let done = false;

  const walk = (node) => {
    if (done) return;

    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.textContent || '';
      if (stop?.container === node) {
        out += raw.slice(0, stop.offset).replace(/\u200B/g, '');
        done = true;
        return;
      }
      out += raw.replace(/\u200B/g, '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    if (node.hasAttribute?.('data-large-tail')) {
      if (pointInside(node, stop)) done = true;
      return;
    }
    if (node.hasAttribute?.('data-doc-math') || node.dataset?.mathExpr) {
      if (pointInside(node, stop)) {
        done = true;
        return;
      }
      out += wrapMathValueToken(node.getAttribute('data-math-expr') || '');
      return;
    }
    if (node.dataset?.varRef) {
      if (pointInside(node, stop)) {
        done = true;
        return;
      }
      out += `{{${node.dataset.varRef}}}`;
      return;
    }
    if (node.dataset?.varInsertMark || node.dataset?.mathInsertMark) {
      if (pointInside(node, stop)) done = true;
      return;
    }
    if (node.tagName === 'BR') {
      if (stop?.container === node) {
        done = true;
        return;
      }
      const prev = neighbor(node, 'previousSibling');
      const next = neighbor(node, 'nextSibling');
      // A trailing caret <br>, or the break Chrome inserts between a nested
      // chip and a path suffix that continues on the next line.
      if (!next) return;
      if (isAtomicChip(prev) && isPathContinuation(next)) return;
      out += '\n';
      return;
    }
    if (
      (node.tagName === 'DIV' || node.tagName === 'P') &&
      out.length > 0 &&
      !out.endsWith('\n')
    ) {
      const prev = neighbor(node, 'previousSibling');
      const lead = (node.textContent || '').replace(/^\u200B+/, '');
      if (!(isAtomicChip(prev) && /^[\\/]/.test(lead))) out += '\n';
    }
    if (stop?.container === node) {
      const kids = node.childNodes;
      const limit = Math.max(0, Math.min(stop.offset, kids.length));
      for (let i = 0; i < limit; i += 1) walk(kids[i]);
      done = true;
      return;
    }
    node.childNodes.forEach(walk);
  };

  if (stop?.container === root) {
    const kids = root.childNodes;
    const limit = Math.max(0, Math.min(stop.offset, kids.length));
    for (let i = 0; i < limit; i += 1) walk(kids[i]);
  } else {
    root.childNodes.forEach(walk);
  }
  return out.replace(/\u00a0/g, ' ');
};

/** contenteditable DOM → stored string (`{{name}}` for chips). */
export const nestedHtmlToValue = (root) => serializeUntil(root, null);

const rangesOverlap = (range, node) => {
  try {
    const other = document.createRange();
    other.selectNode(node);
    return (
      range.compareBoundaryPoints(Range.END_TO_START, other) > 0 &&
      range.compareBoundaryPoints(Range.START_TO_END, other) < 0
    );
  } catch {
    return false;
  }
};

/**
 * Selection as offsets into `nestedHtmlToValue` + hidden tail.
 * `coversTail` — the selection replaces the hidden remainder (select-all,
 * or a range that includes the "… more" marker).
 */
export const valueCaretOffsets = (root) => {
  if (!root) return { start: 0, end: 0, coversTail: false };
  const sel = window.getSelection();
  if (!sel?.rangeCount || !root.contains(sel.anchorNode)) {
    const n = nestedHtmlToValue(root).length;
    return { start: n, end: n, coversTail: false };
  }
  const range = sel.getRangeAt(0);
  const start = serializeUntil(root, {
    container: range.startContainer,
    offset: range.startOffset,
  }).length;
  const end = range.collapsed
    ? start
    : serializeUntil(root, {
        container: range.endContainer,
        offset: range.endOffset,
      }).length;
  const from = Math.min(start, end);
  const to = Math.max(start, end);
  const marker = root.querySelector('[data-large-tail]');
  let coversTail = false;
  if (marker) {
    coversTail = rangesOverlap(range, marker);
    if (!range.collapsed && from === 0 && to === nestedHtmlToValue(root).length) {
      coversTail = true;
    }
  }
  return { start: from, end: to, coversTail };
};
